import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Typography,
  Menu,
  Tooltip,
  Image,
  theme,
  Button,
} from "antd";
import { DownOutlined, LeftOutlined, UpOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useHorizontalScrollAffordance from "../../hooks/useHorizontalScrollAffordance";
import useTabHelperLabel, { resolveTabAnchor } from "../../hooks/useTabHelperLabel";

import billingMenu from "../../assets/newIcons/bill 3.svg";
import activeBilling from "../../assets/newIcons/bill 3 Active.svg";
import packagesMenu from "../../assets/newIcons/PAckage.svg";
import activePackages from "../../assets/newIcons/PAckage Active.svg";
import servicesMenu from "../../assets/newIcons/Services.svg";
import activeServices from "../../assets/newIcons/Services Active.svg";
import GalleryIcon from "../../assets/newIcons/gallery 1.svg";
import GalleryActiveIcon from "../../assets/newIcons/gallery 1 Active.svg";
import SummaryIcon from "../../assets/newIcons/SUmmary.svg";
import SummaryIconActive from "../../assets/newIcons/SUmmary Active.svg";
import { UiTag } from "../ui";
import { formatPatientAge } from "../../helpers/formatAge";
import { measureCompactPatientHeaderBottom } from "../../helpers/utility";
import "./patientHeader.css";

const { useToken } = theme;

const COMPACT_TABS_MEDIA = "(max-width: 768px)";
const TABLET_TABS_MEDIA = "(min-width: 769px) and (max-width: 1280px)";

const OP_TAB_DEFS = [
  { key: "billing", activeIcon: activeBilling, inactiveIcon: billingMenu, title: "Billing" },
  { key: "packages", activeIcon: activePackages, inactiveIcon: packagesMenu, title: "Packages" },
  { key: "services", activeIcon: activeServices, inactiveIcon: servicesMenu, title: "Services" },
  { key: "gallery", activeIcon: GalleryActiveIcon, inactiveIcon: GalleryIcon, title: "Gallery" },
  { key: "summary", activeIcon: SummaryIconActive, inactiveIcon: SummaryIcon, title: "Summary" },
];

const CompactTabItem = ({ tab, isActive, onShowLabel, onSelect }) => (
  <div className="mobile-tab-item-wrap">
    <div
      className={`mobile-tab-item ${isActive ? "active" : ""}`}
      onClick={(e) => {
        onShowLabel(tab.key, tab.title, e.currentTarget);
        onSelect(tab.key);
      }}
    >
      {tab.label}
    </div>
  </div>
);

const getOpTabs = (selectedTab, token, options = {}) => {
  const { hideTooltips = false, compactIcons = false } = options;
  const activeStyles = {
    height: compactIcons ? "42px" : "50px",
    width: "auto",
    marginBottom: compactIcons ? "4px" : "8px",
    background: token.colorPrimary,
    padding: compactIcons ? "8px" : "10px",
    borderRadius: "6px",
  };
  const inactiveStyles = {
    height: compactIcons ? "42px" : "50px",
    width: "auto",
    marginBottom: compactIcons ? "4px" : "8px",
    background: "#fff",
    padding: compactIcons ? "8px" : "10px",
    borderRadius: "6px",
    border: "1px solid #D9D9D9",
  };

  const menuImages = (icon, styleObj, title) => {
    const iconNode = (
      <span
        role="img"
        aria-label={title}
        className="anticon anticon-user ant-menu-item-icon"
      >
        <Image src={icon} preview={false} style={styleObj} />
      </span>
    );

    if (hideTooltips) return iconNode;

    return (
      <Tooltip title={title} trigger={["hover"]} mouseEnterDelay={0.15}>
        {iconNode}
      </Tooltip>
    );
  };

  const tabIconStyle = (isActive) => (isActive ? activeStyles : inactiveStyles);

  return OP_TAB_DEFS.map(({ key, activeIcon, inactiveIcon, title }) => ({
    key,
    tabTitle: title,
    label: menuImages(
      selectedTab === key ? activeIcon : inactiveIcon,
      tabIconStyle(selectedTab === key),
      title
    ),
  }));
};

const OpMenu = ({ patientData, navigate, params, onTabClick }) => {
  const capitalize = (text = "") =>
    text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  const details = patientData || {};
  const { token } = useToken();

  const [isCompactTabs, setIsCompactTabs] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(COMPACT_TABS_MEDIA).matches
      : false
  );
  const [isTabletTabs, setIsTabletTabs] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(TABLET_TABS_MEDIA).matches
      : false
  );
  const [tabsOpen, setTabsOpen] = useState(true);
  const headerRef = useRef(null);
  const tabsScrollRef = useRef(null);

  const selectedTab = params?.type || "billing";
  const helperLabelsEnabled = isCompactTabs || isTabletTabs;
  const { showTabLabel, TabHelperLabel } = useTabHelperLabel(helperLabelsEnabled);

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_TABS_MEDIA);
    const onChange = () => setIsCompactTabs(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(TABLET_TABS_MEDIA);
    const onChange = () => setIsTabletTabs(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;

    const apply = () => {
      const bottom = measureCompactPatientHeaderBottom(el);
      document.documentElement.style.setProperty(
        "--op-patient-header-height",
        `${bottom}px`
      );
    };

    apply();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", apply);
      return () => {
        window.removeEventListener("resize", apply);
        document.documentElement.style.removeProperty("--op-patient-header-height");
      };
    }
    const ro = new ResizeObserver(() => apply());
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      document.documentElement.style.removeProperty("--op-patient-header-height");
    };
  }, [tabsOpen, details, params?.type, isCompactTabs, isTabletTabs]);

  useEffect(() => {
    const collapsed = isCompactTabs && !tabsOpen;
    document.documentElement.classList.toggle(
      "ip-patient-header-collapsed",
      collapsed
    );
    return () => {
      document.documentElement.classList.remove("ip-patient-header-collapsed");
    };
  }, [isCompactTabs, tabsOpen]);

  useHorizontalScrollAffordance(tabsScrollRef, [
    isCompactTabs,
    isTabletTabs,
    tabsOpen,
    selectedTab,
  ]);

  const navigateToHome = () => {
    if (!navigate) return;
    navigate("/receptionist/out-patients");
  };

  const tabItems = getOpTabs(selectedTab, token, {
    hideTooltips: isCompactTabs || isTabletTabs,
    compactIcons: isCompactTabs || isTabletTabs,
  });

  const handleCompactTabSelect = (key) => {
    onTabClick?.({ key });
  };

  const getTabTitle = (key) => {
    const tab = tabItems.find((item) => item.key === key);
    return tab?.tabTitle || key;
  };

  const revealTabLabel = (key, domEventOrAnchor) => {
    if (!helperLabelsEnabled) return;
    const title = getTabTitle(key);
    const anchor = resolveTabAnchor(domEventOrAnchor);
    if (!title || !anchor) return;
    window.requestAnimationFrame(() => {
      showTabLabel(key, title, anchor);
    });
  };

  const handleMenuPointerUp = (e) => {
    if (!helperLabelsEnabled || isCompactTabs) return;
    const menuItem = e.target.closest?.(".ant-menu-item");
    if (!menuItem) return;
    const menuRoot = menuItem.closest(".ant-menu");
    const menuItems = menuRoot?.querySelectorAll(".ant-menu-item");
    if (!menuItems?.length) return;
    const index = Array.from(menuItems).indexOf(menuItem);
    const tab = tabItems[index];
    if (!tab) return;
    revealTabLabel(tab.key, menuItem);
  };

  const handleMenuClick = (info) => {
    if (helperLabelsEnabled && !isCompactTabs) {
      revealTabLabel(info.key, info.domEvent);
    }
    onTabClick?.(info);
  };

  const patientFullName = `${capitalize(details?.first_name || "")} ${
    details?.last_name || ""
  }`.trim();

  return (
    <>
      <Row
        ref={headerRef}
        className={`patient-menu-wrapper ${
          isCompactTabs ? "patient-menu-wrapper--compact-tabs" : ""
        } ${
          isTabletTabs && !isCompactTabs
            ? "patient-menu-wrapper--tablet-tabs"
            : ""
        } ${
          isCompactTabs && !tabsOpen ? "patient-menu-wrapper--tabs-collapsed" : ""
        }`}
        gutter={isCompactTabs ? [12, 0] : [12, 12]}
        style={{
          position: "fixed",
          top: "0",
          zIndex: "1000",
          backgroundColor: token.colorBgContainer,
          padding: "6px 16px 2px",
          boxShadow: "0 2px 4px -4px #222",
          width: "100%",
          maxWidth: "100vw",
          boxSizing: "border-box",
        }}
      >
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={6}
          xl={6}
          className="patient-menu-info-col"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              marginLeft: "8px",
              marginTop: "4px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "6px",
              }}
            >
              <LeftOutlined
                onClick={() => navigateToHome()}
                style={{
                  fontSize: "18px",
                  padding: "0px 6px",
                  marginTop: "6px",
                  cursor: "pointer",
                }}
              />
              <Tooltip
                title={patientFullName}
                open={isCompactTabs ? false : undefined}
              >
                <Typography.Text
                  className="patient-name"
                  onClick={(e) => {
                    if (!isCompactTabs) return;
                    if (patientFullName) {
                      showTabLabel(
                        "patient-name",
                        patientFullName,
                        e.currentTarget
                      );
                    }
                  }}
                  style={{
                    color: token.colorText,
                    fontSize: "16px",
                    fontWeight: 600,
                    maxWidth: isCompactTabs ? "min(70vw, 280px)" : undefined,
                    whiteSpace: isCompactTabs ? "normal" : "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: isCompactTabs ? "-webkit-box" : "inline-block",
                    WebkitLineClamp: isCompactTabs ? 2 : undefined,
                    WebkitBoxOrient: isCompactTabs ? "vertical" : undefined,
                    verticalAlign: "middle",
                    lineHeight: isCompactTabs ? 1.25 : undefined,
                    cursor: isCompactTabs ? "pointer" : undefined,
                  }}
                >
                  {patientFullName}
                </Typography.Text>
              </Tooltip>
            </div>

            <div className="patient-header-meta-row">
              {(details?.age || details?.gender) && (
                <UiTag tone="neutral" size="sm">
                  {formatPatientAge(details?.age, details?.gender)}
                </UiTag>
              )}
              {details?.op_id && (
                <UiTag tone="neutral" size="sm">
                  OP ID: {details.op_id}
                </UiTag>
              )}
              {details?.admission_date && (
                <UiTag tone="neutral" size="sm">
                  <CalendarOutlined style={{ color: token.colorPrimary }} /> DOA:{" "}
                  {dayjs(details.admission_date).format("DD-MM-YYYY")}
                </UiTag>
              )}
            </div>
          </div>

          {isCompactTabs && !tabsOpen && (
            <div className="op-patient-tabs-toggle-row ip-patient-tabs-toggle-row ip-patient-tabs-toggle-row--collapsed">
              <Button
                type="text"
                className="op-patient-tabs-toggle op-patient-tabs-toggle--bottom"
                style={{ color: token.colorPrimary }}
                aria-label="Expand tab icons"
                onClick={() => setTabsOpen(true)}
                icon={<DownOutlined />}
              />
            </div>
          )}
        </Col>

        {(!isCompactTabs || tabsOpen) && (
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={18}
          xl={18}
          xxl={18}
          className="patient-menu-tabs-col"
        >
          {isCompactTabs ? (
            <>
              <div
                ref={tabsScrollRef}
                className="mobile-tab-scroll ip-compact-patient-tabs"
              >
                {tabItems.map((tab) => (
                  <CompactTabItem
                    key={tab.key}
                    tab={{ ...tab, title: tab.tabTitle }}
                    isActive={selectedTab === tab.key}
                    onShowLabel={showTabLabel}
                    onSelect={handleCompactTabSelect}
                  />
                ))}
              </div>
              <div className="op-patient-tabs-toggle-row ip-patient-tabs-toggle-row">
                <Button
                  type="text"
                  className="op-patient-tabs-toggle op-patient-tabs-toggle--bottom"
                  style={{ color: token.colorPrimary }}
                  aria-label="Collapse tab icons"
                  onClick={() => setTabsOpen(false)}
                  icon={<UpOutlined />}
                />
              </div>
            </>
          ) : (
            <div
              ref={tabsScrollRef}
              className="patient-menu-scroll"
              onPointerUp={handleMenuPointerUp}
            >
              <Menu
                className="patientMenu"
                style={{ justifyContent: "flex-start" }}
                onClick={handleMenuClick}
                selectedKeys={[selectedTab]}
                mode="horizontal"
                disabledOverflow
                items={tabItems}
              />
            </div>
          )}
        </Col>
        )}
      </Row>
      {TabHelperLabel}
    </>
  );
};

export default OpMenu;
